import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from '@/app/providers/ThemeContext';
import { LanguageProvider } from '@/app/providers/LanguageContext';
import { NotificationProvider as ToastNotificationProvider } from '@/components/NotificationSystem';
import { NotificationProvider } from '@/app/providers/NotificationContext';
import { PendingApprovalsProvider } from '@/app/providers/PendingApprovalsContext';
import { SocketProvider } from '@/app/providers/SocketProvider';
import { useAuth } from '@/app/providers/AuthContext';
import { EmployeeProvider } from '@/features/employees/hooks/useEmployee';
import { AUTH_CONFIG } from '@/shared/config/constants';
import authService from '@/features/auth/services/authService';
import {
  clearAuthStorage,
  getRefreshToken,
  isAccessTokenFresh,
  refreshSession,
  scheduleTokenRefresh,
} from '@/shared/lib/authSession';
import { applyAccessTokenClaims, hydrateSessionUser, persistUser } from '@/shared/lib/teamLeaderAccess';
import { handlePreLoginError } from '@/shared/lib/advancedErrorHandler';
import AppRoutes from '@/app/router/AppRoutes';
import LayoutWrapper from '@/app/layout/LayoutWrapper';
import EasterEggs from '@/EasterEggs ';
import '@/index.css';
import TurnOffLogs from '@/TurnoffLogs';
import Loading from '@/shared/components/Loading';
import { initSupportDiagnostics } from '@/utils/supportClientContext';

// Initialize app-level code once per app load (not per mount)
// Using module-level guard to ensure initialization happens only once
let appInitialized = false;
if (!appInitialized) {
  appInitialized = true;
  EasterEggs();
  TurnOffLogs();
  initSupportDiagnostics();
}


function App() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restoreSessionSilently = async () => {
      const savedUser = localStorage.getItem(AUTH_CONFIG.USER_STORAGE_KEY);
      const token = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
      const refreshToken = getRefreshToken();

      if (!savedUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      if (!token && !refreshToken) {
        clearAuthData();
        setLoading(false);
        return;
      }

      try {
        let userData = JSON.parse(savedUser);

        if (!isAccessTokenFresh(token) && refreshToken) {
          try {
            await refreshSession();
          } catch (refreshError) {
            console.warn('Silent refresh failed on startup:', refreshError.message);
            clearAuthData();
            setLoading(false);
            return;
          }
        } else if (isAccessTokenFresh(token)) {
          scheduleTokenRefresh();
        }

        try {
          userData = await hydrateSessionUser(userData);
          persistUser(userData);
        } catch (permissionError) {
          console.warn('Could not load team leader permissions:', permissionError?.message || permissionError);
        }

        setUser(userData);
        setLoading(false);
      } catch (error) {
        const errorInfo = handlePreLoginError(error);

        if (errorInfo.shouldShow) {
          console.error('Auth check error:', errorInfo.message || error.message);
        }

        clearAuthData();
        setLoading(false);
      }
    };

    restoreSessionSilently();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; setUser is stable
  }, []);

  const clearAuthData = () => {
    clearAuthStorage();
    setUser(null);
  };

  const handleLogin = (userData) => {
    const nextUser = persistUser(applyAccessTokenClaims(userData));
    setUser(nextUser);
    scheduleTokenRefresh();
  };

  const handleLogout = async () => {
    try {
      const response = await authService.logout();

      if (response?.success && response?.message) {
        console.log('✅', response.message);
      }
    } catch (error) {
      console.error('Logout error:', error);
      clearAuthData();
    } finally {
      clearAuthData();
      window.location.href = '/';
    }
  };

  if (loading) {
    return (
      <ThemeProvider>
        <Loading fullPage size="lg" color="primary" />
      </ThemeProvider>
    );
  }

  return (
    <EmployeeProvider>
      <LanguageProvider>
        <ThemeProvider>
          <SocketProvider>
            <NotificationProvider>
              <PendingApprovalsProvider>
                <ToastNotificationProvider>
                  <Router
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true,
                      v7_fetcherPersist: true,
                      v7_normalizeFormMethod: true,
                      v7_partialHydration: true,
                      v7_skipActionErrorRevalidation: true,
                    }}
                  >
                    <LayoutWrapper user={user} onLogout={handleLogout}>
                      <AppRoutes user={user} onLogout={handleLogout} onLogin={handleLogin} />
                    </LayoutWrapper>
                  </Router>
                </ToastNotificationProvider>
              </PendingApprovalsProvider>
            </NotificationProvider>
          </SocketProvider>
        </ThemeProvider>
      </LanguageProvider>
    </EmployeeProvider>
  );
}

export default App;
